import * as types from '../types';

/**
 * The plan an adopter gets: the six-month Mini plan in the site's default tier. One definition, so
 * the activation fee the page quotes and the plan the one-page checkout puts in the cart cannot drift.
 */
export function rescuePlan(plans: types.ISubscriptionProduct[], miniPlusByDefault: boolean) {
  return plans.find(
    (plan) =>
      plan.renewalMonths === 6 && !plan.freeTrial && !plan.thirdParty && !!plan.isPlusPlan === miniPlusByDefault,
  );
}

export interface RescueBundleSkus {
  miniSku?: string;
  planSku?: string;
}

/**
 * The adoption bundle exactly: the Mini on the rescue plan, bought for the adopter's own pet. Tests the
 * plan line directly rather than through `isBundleCartItem`: `lib/cart` pulls in the api client, and
 * this module is read by hooks whose tests should not have to load it.
 */
function isRescueBundle(item: types.CartItem, { miniSku, planSku }: RescueBundleSkus) {
  return (
    'subscriptionLineItem' in item &&
    !!miniSku &&
    !!planSku &&
    item.lineItem.sku === miniSku &&
    item.subscriptionLineItem.sku === planSku &&
    !item.isGift
  );
}

/** The cart the one-page checkout buys: the adoption bundle and nothing else. */
export function cartHoldsRescueBundle(cart: types.Cart, skus: RescueBundleSkus) {
  const items = Object.values(cart.cartItems);
  return items.length === 1 && isRescueBundle(items[0], skus);
}

/**
 * Whether the one-page checkout can take this cart: empty (it adds the bundle itself) or already just
 * the bundle. The cart persists between visits and the page has no Edit cart, so anything else - a
 * leftover accessory, another plan, a gift - keeps the adopter on the pdp, where the bag can change it.
 */
export function cartFitsRescueCheckout(cart: types.Cart, skus: RescueBundleSkus) {
  return Object.keys(cart.cartItems).length === 0 || cartHoldsRescueBundle(cart, skus);
}

/**
 * Who the one-page checkout (and its A/B test) is for: rescue adopters buying for their own pet, whose
 * link named the species (older links carry no `?type=`, and the page has no species chooser), with a
 * cart the page can take. Everyone else keeps the pdp and stays out of the test.
 */
export function rescueOnePageEligible({
  rescueAdopter,
  isGift,
  speciesNamed,
  cartFits,
}: {
  rescueAdopter: boolean;
  isGift: boolean;
  speciesNamed: boolean;
  cartFits: boolean;
}) {
  return rescueAdopter && !isGift && speciesNamed && cartFits;
}
